import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../App';
import Layout from '../components/Layout';
import { FileText, MessageSquare, Users, TrendingUp, Eye, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';

const Dashboard = ({ user, setUser }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API_URL}/analytics/overview`);
      setAnalytics(res.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    {
      icon: FileText,
      label: 'Total Documents',
      value: analytics?.total_documents || 0,
      color: 'bg-violet-100 text-violet-600'
    },
    {
      icon: Users,
      label: 'Team Members',
      value: analytics?.total_users || 0,
      color: 'bg-teal-100 text-teal-600'
    },
    {
      icon: MessageSquare,
      label: 'AI Conversations',
      value: analytics?.total_chats || 0,
      color: 'bg-zinc-100 text-zinc-600'
    }
  ];

  return (
    <Layout user={user} setUser={setUser}>
      <div className="p-8" data-testid="dashboard">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>
            Welcome back, {user.name}!
          </h1>
          <p className="text-zinc-600" style={{ fontFamily: 'Inter, sans-serif' }}>
            Here's what's happening with your knowledge base
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="bg-white border border-zinc-200 rounded-lg p-6 hover:shadow-sm transition-all duration-200"
              data-testid={`stat-card-${index}`}
            >
              <div className="flex items-center gap-4">
                <div className={`h-12 w-12 rounded-lg ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {stat.value}
                  </div>
                  <div className="text-sm text-zinc-600" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {stat.label}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bento Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Documents */}
          <div className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="recent-documents-section">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Recent Documents
              </h2>
              <Link to="/documents/new">
                <Button size="sm" className="bg-violet-600 hover:bg-violet-700" data-testid="create-document-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Create
                </Button>
              </Link>
            </div>
            <div className="space-y-3">
              {loading ? (
                <div className="text-zinc-500 text-sm">Loading...</div>
              ) : analytics?.recent_documents?.length > 0 ? (
                analytics.recent_documents.map((doc) => (
                  <Link
                    key={doc.id}
                    to={`/documents/${doc.id}`}
                    className="block p-4 border border-zinc-200 rounded-lg hover:border-violet-300 hover:bg-violet-50/50 transition-all duration-200"
                    data-testid={`recent-doc-${doc.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-zinc-900 mb-1">{doc.title}</h3>
                        <div className="text-sm text-zinc-600">
                          {doc.category && (
                            <span className="inline-flex items-center px-2 py-1 rounded-md bg-zinc-100 text-xs mr-2">
                              {doc.category}
                            </span>
                          )}
                          <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="text-center py-8 text-zinc-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
                  <p>No documents yet</p>
                  <Link to="/documents/new">
                    <Button size="sm" className="mt-4 bg-violet-600 hover:bg-violet-700">
                      Create your first document
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Popular Documents */}
          <div className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="popular-documents-section">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Most Viewed
              </h2>
              <TrendingUp className="h-5 w-5 text-zinc-400" />
            </div>
            <div className="space-y-3">
              {loading ? (
                <div className="text-zinc-500 text-sm">Loading...</div>
              ) : analytics?.popular_documents?.length > 0 ? (
                analytics.popular_documents.map((doc) => (
                  <Link
                    key={doc.id}
                    to={`/documents/${doc.id}`}
                    className="block p-4 border border-zinc-200 rounded-lg hover:border-teal-300 hover:bg-teal-50/50 transition-all duration-200"
                    data-testid={`popular-doc-${doc.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-zinc-900 mb-1">{doc.title}</h3>
                        <div className="flex items-center gap-2 text-sm text-zinc-600">
                          <Eye className="h-4 w-4" />
                          <span>{doc.views} views</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="text-center py-8 text-zinc-500">
                  <TrendingUp className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
                  <p>No views yet</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 bg-gradient-to-br from-violet-50 to-white border border-violet-200 rounded-lg p-6">
          <h2 className="text-xl font-bold text-zinc-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/documents/new" data-testid="quick-action-create">
              <Button variant="outline" className="w-full justify-start h-auto p-4 hover:bg-white hover:border-violet-300">
                <FileText className="h-5 w-5 mr-3 text-violet-600" />
                <div className="text-left">
                  <div className="font-semibold text-zinc-900">Create Document</div>
                  <div className="text-xs text-zinc-600">Start writing new content</div>
                </div>
              </Button>
            </Link>
            <Link to="/chat" data-testid="quick-action-chat">
              <Button variant="outline" className="w-full justify-start h-auto p-4 hover:bg-white hover:border-violet-300">
                <MessageSquare className="h-5 w-5 mr-3 text-violet-600" />
                <div className="text-left">
                  <div className="font-semibold text-zinc-900">Ask AI Assistant</div>
                  <div className="text-xs text-zinc-600">Get instant answers</div>
                </div>
              </Button>
            </Link>
            <Link to="/documents" data-testid="quick-action-browse">
              <Button variant="outline" className="w-full justify-start h-auto p-4 hover:bg-white hover:border-violet-300">
                <FileText className="h-5 w-5 mr-3 text-violet-600" />
                <div className="text-left">
                  <div className="font-semibold text-zinc-900">Browse Library</div>
                  <div className="text-xs text-zinc-600">View all documents</div>
                </div>
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;